/**
 * product-atf.js
 * JavaScript for the Main Product ATF v2 section.
 *
 * Sections:
 *   0. Glide no-rewind patch  — disables looping on the mobile slider
 *   1. AtfSection             — main product form logic (toggle, cards, ATC, gallery)
 *   2. initMobileZoom         — mobile-only full-screen image zoom overlay
 *   3. initDesktopLightboxNav — prev/next navigation for the desktop lightbox
 *   4. initDropdowns          — accordion open/close
 *   5. initStickyCTA          — sticky bottom bar
 */

/* =============================================================================
   0. Glide no-rewind patch (ATF section only)
   Both this file and product-min.js are defer scripts, so they both execute
   before DOMContentLoaded fires. product-min.js registers its DOMContentLoaded
   callback first, then this file runs and wraps window.Glide. When
   DOMContentLoaded fires, product-min.js's callback calls new Glide(…) and
   hits the patched class, which injects rewind:false for any selector that
   lives inside .mp-atf-section.
============================================================================= */
(function () {
  if (typeof window.Glide === 'undefined') return;
  var _Glide = window.Glide;
  function PatchedGlide(selector, options) {
    var el = typeof selector === 'string' ? document.querySelector(selector) : selector;
    if (el && el.closest && el.closest('.mp-atf-section')) {
      options = Object.assign({}, options, { rewind: false });
    }
    return new _Glide(selector, options);
  }
  PatchedGlide.prototype = _Glide.prototype;
  Object.setPrototypeOf(PatchedGlide, _Glide);
  window.Glide = PatchedGlide;
}());

/* =============================================================================
   1. AtfSection
   Manages the above-the-fold product form: purchase-type toggle, variant-card
   selection, hidden form input sync, gallery thumbnail switching, and ATC submit.
============================================================================= */

class AtfSection {
  constructor(root) {
    this.root          = root;
    this.cards         = root.querySelector('[data-cards]');
    this.toggle        = root.querySelector('[data-purchase-toggle]');
    this.variantInput  = root.querySelector('[data-variant-input]');
    this.planInput     = root.querySelector('[data-selling-plan-input]');
    this.atcPriceEl    = root.querySelector('[data-atc-price]');
    this.thumbs        = root.querySelectorAll('[data-thumb-id]');
    this.mediaItems    = root.querySelectorAll('[data-media-id]');
    this.form          = root.querySelector('.mp-atf__form');
    this.submitButton  = root.querySelector('.mp-atf__atc');
    this.errorWrapper  = root.querySelector('.product-form__error-message-wrapper');
    this.errorMessage  = root.querySelector('.product-form__error-message');
    this.status        = root.querySelector('[data-atf-status]'); // polite live region
    this.mode               = this.cards ? this.cards.dataset.purchaseMode : 'one_time';
    this.submitAction       = root.dataset.submit_action || 'mini-cart';
    this._lastSubCardIndex  = null; // remembers last selected subscription card index

    this._bind();
    this._ensureValidSelection();
    this._syncFromSelected();
    this._initLastSubCardIndex(); // capture initial sub card selection
  }

  /* ── Event binding ────────────────────────────────────────────────────── */
  _bind() {
    // Skip link → move focus to the product info. Some browsers don't reliably
    // focus a fragment target with tabindex="-1" on their own, so do it here.
    const skipLink = this.root.querySelector('.mp-atf__skip-link');
    if (skipLink) {
      skipLink.addEventListener('click', (e) => {
        const target = document.getElementById('ProductInfo-' + this.root.dataset.section);
        if (!target) return;
        e.preventDefault();
        target.focus();
      });
    }

    // Purchase-type toggle buttons
    if (this.toggle) {
      this.toggle.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-purchase-type]');
        if (btn) this.setMode(btn.dataset.purchaseType);
      });
    }

    // Variant card selection
    if (this.cards) {
      this.cards.addEventListener('click', (e) => {
        const card = e.target.closest('[data-card]');
        if (card && !card.classList.contains('is-soldout')) this._selectCard(card);
      });

      // Keyboard selection: arrow keys / Space on a native radio fire `change`,
      // not `click`, so the visual + form state must be synced here too.
      this.cards.addEventListener('change', (e) => {
        const radio = e.target.closest('[data-card-radio]');
        if (!radio) return;
        const card = radio.closest('[data-card]');
        if (card && !card.classList.contains('is-soldout')) this._selectCard(card);
      });
    }

    // Inline links that switch purchase mode (e.g. "Subscribe to stay consistent")
    // CHANGE 1: Added e.stopPropagation() to prevent the click bubbling up to
    // the parent <label>/card and re-triggering card selection logic mid-switch.
    // CHANGE 2: Reads data-target-card index from the link so setMode knows
    // exactly which subscription card to land on.
    this.root.addEventListener('click', (e) => {
      const link = e.target.closest('[data-purchase-type]');
      if (!link || link.closest('[data-purchase-toggle]')) return;
      e.preventDefault();
      e.stopPropagation(); // ← prevents parent label/card from also firing
      const targetIdx = link.dataset.targetCard !== undefined
        ? parseInt(link.dataset.targetCard, 10)
        : null;
      this.setMode(link.dataset.purchaseType, targetIdx);
      // This link lived inside the one-time card, which is now hidden, so its
      // focus would be lost. Move focus to the newly selected card instead.
      const newRadio = this.cards?.querySelector(
        `[data-card].is-selected[data-card-mode="${this.mode}"] [data-card-radio]`
      );
      if (newRadio) newRadio.focus();
    });

    // Desktop thumbnail clicks
    this.thumbs.forEach((t) => {
      t.addEventListener('click', () => this._showMedia(t.dataset.thumbId));
    });

    // Mobile: scroll-snapping thumbnails update active state
    const thumbsContainer = this.root.querySelector('.mp-atf__thumbs');
    if (thumbsContainer) {
      thumbsContainer.addEventListener('scroll', () => {
        if (window.innerWidth >= 1080) return;
        const center = thumbsContainer.scrollLeft + thumbsContainer.offsetWidth / 2;
        let closest = null, minDist = Infinity;
        this.thumbs.forEach((t) => {
          const dist = Math.abs((t.offsetLeft + t.offsetWidth / 2) - center);
          if (dist < minDist) { minDist = dist; closest = t; }
        });
        if (closest) {
          this.thumbs.forEach((t) => t.classList.remove('is-active'));
          closest.classList.add('is-active');
        }
      }, { passive: true });
    }

    // Rating button → scroll to reviews widget
    // if (this.ratingTrigger) {
    //   this.ratingTrigger.addEventListener('click', () => this._scrollToReviews());
    // }

    // Form submit
    if (this.form) {
      this.form.addEventListener('submit', (e) => this._handleAddToCart(e));
    }

    // Buy Once link — buys the one-time variant outright. Bound before the
    // generic [data-purchase-type] delegation would ever see it (the link
    // carries no such attribute), so it adds to cart instead of switching tabs.
    const buyOnceLink = this.root.querySelector('[data-buy-once-link]');
    if (buyOnceLink) {
      buyOnceLink.addEventListener('click', (e) => this._handleBuyOnce(e, buyOnceLink));
    }
  }

  /* ── Buy Once ────────────────────────────────────────────────────────── */
  /**
   * Adds the one-time variant to the cart directly, bypassing the card
   * selection entirely: no selling_plan is ever sent, and the subscription
   * cards keep whatever the shopper had selected. Mirrors _handleAddToCart —
   * same gift bundling, same error surface, same /cart destination.
   */
  async _handleBuyOnce(event, link) {
    event.preventDefault();
    event.stopPropagation();

    if (link.disabled || link.classList.contains('is-loading')) return;

    const variantId = link.dataset.variantId;
    if (!variantId) return;

    link.classList.add('is-loading');
    link.setAttribute('aria-busy', 'true');
    if (this.errorWrapper) this.errorWrapper.hidden = true;

    const main = { id: parseInt(variantId, 10), quantity: 1 };

    try {
      // GiftBundle decides eligibility itself from (variant, sellingPlan);
      // passing no selling plan means a subscription-only gift won't attach.
      const items = (window.GiftBundle && window.GiftBundle.augment)
        ? await window.GiftBundle.augment([main])
        : [main];

      let response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });

      // Same atomic-add fallback as the main CTA: never let a gift stock issue
      // block the purchase.
      if (!response.ok && items.length > 1) {
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [main] }),
        });
      }

      const data = await response.json();

      if (!response.ok) {
        this._showError(data.description || 'An error occurred. Please try again.');
        return;
      }

      window.location.href = '/cart';

    } catch (error) {
      console.error('Buy once error:', error);
      this._showError('Unable to add product to cart. Please try again.');
    } finally {
      link.classList.remove('is-loading');
      link.removeAttribute('aria-busy');
    }
  }

  /* ── Add to cart ─────────────────────────────────────────────────────── */
  async _handleAddToCart(event) {
    event.preventDefault();

    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('loading');
    }

    if (this.errorWrapper) this.errorWrapper.hidden = true;

    const variantId    = this.variantInput?.value;
    const sellingPlan  = this.planInput?.value;

    if (!variantId) {
      this._showError('Please select a product option.');
      if (this.submitButton) this.submitButton.disabled = false;
      return;
    }

    const main = { id: parseInt(variantId, 10), quantity: 1 };
    if (sellingPlan) main.selling_plan = parseInt(sellingPlan, 10);

    try {
      // Bundle the free gift (if this qualifies) into the SAME add — before /cart.
      const items = (window.GiftBundle && window.GiftBundle.augment)
        ? await window.GiftBundle.augment([main])
        : [main];

      let response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      // /cart/add.js is atomic: if the bundled gift is unavailable the whole add
      // fails. Never let a gift stock issue block the purchase — retry product only.
      if (!response.ok && items.length > 1) {
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ items: [main] }),
        });
      }

      const data = await response.json();

      if (!response.ok) {
        this._showError(data.description || 'An error occurred. Please try again.');
        return;
      }

      window.location.href = '/cart';

    } catch (error) {
      console.error('Add to cart error:', error);
      this._showError('Unable to add product to cart. Please try again.');
    } finally {
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('loading');
      }
    }
  }

  _showError(message) {
    if (this.errorWrapper && this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorWrapper.hidden = false;
    } else {
      alert(message);
    }
  }

  /* ── Mode & card selection ───────────────────────────────────────────── */

  /** Captures the initially selected subscription card index on page load. */
  _initLastSubCardIndex() {
    if (!this.cards) return;
    const subCards = Array.from(
      this.cards.querySelectorAll('[data-card][data-card-mode="subscription"]')
    ).filter((c) => !c.classList.contains('is-soldout'));
    const idx = subCards.findIndex((c) => c.classList.contains('is-selected'));
    if (idx !== -1) this._lastSubCardIndex = idx;
  }

  /** Returns non-soldout cards for the current mode. */
  _visibleCards() {
    if (!this.cards) return [];
    return Array.from(this.cards.querySelectorAll('[data-card]'))
      .filter((c) => c.dataset.cardMode === this.mode && !c.classList.contains('is-soldout'));
  }

  /** Ensures at least one card is selected for the active mode. */
  _ensureValidSelection() {
    const visible = this._visibleCards();
    if (!visible.length) return;
    if (visible.some((c) => c.classList.contains('is-selected'))) return;

    this.cards.querySelectorAll('[data-card].is-selected')
      .forEach((c) => c.classList.remove('is-selected'));

    visible[0].classList.add('is-selected');
    const radio = visible[0].querySelector('[data-card-radio]');
    if (radio) radio.checked = true;
  }

  /**
   * Switches the purchase mode and re-syncs.
   *
   * CHANGE 3: Accepts an optional targetCardIndex (0-based) so callers like
   * the "Subscribe to stay consistent" inline link can specify exactly which
   * card to land on in the new mode, instead of always falling back to the
   * first card or a title-match heuristic.
   *
   * Priority order for card selection:
   *   1. Explicit targetCardIndex (from inline link's data-target-card)
   *   2. Title match (for toggle tab switching — preserves size selection)
   *   3. _ensureValidSelection fallback (first available card)
   */
  setMode(mode, targetCardIndex = null) {
    if (mode !== 'subscription' && mode !== 'one_time') return;

    // Remember previous selection by title (used for tab toggle matching)
    const prevSelected = this.cards?.querySelector(
      `[data-card].is-selected[data-card-mode="${this.mode}"]`
    );
    const prevTitle = prevSelected
      ?.querySelector('.mp-atf__card-title')
      ?.textContent.trim();

    this.mode = mode;
    if (this.cards) this.cards.dataset.purchaseMode = mode;

    if (this.toggle) {
      this.toggle.querySelectorAll('[data-purchase-type]').forEach((b) => {
        const active = b.dataset.purchaseType === mode;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    // Announce the swap to screen readers (setMode only runs on user action).
    if (this.status) {
      this.status.textContent = mode === 'subscription'
        ? 'Subscribe and save options shown.'
        : 'One-time purchase options shown.';
    }

    // All available cards for the new mode
    const newCards = this.cards
      ? Array.from(this.cards.querySelectorAll(`[data-card][data-card-mode="${mode}"]`))
          .filter((c) => !c.classList.contains('is-soldout'))
      : [];

    let target = null;

    if (targetCardIndex !== null) {
      // Explicit index wins — used by inline "Subscribe to stay consistent" links
      target = newCards[targetCardIndex] ?? newCards[0];
      // Also persist this as the last known subscription card index
      if (mode === 'subscription') this._lastSubCardIndex = targetCardIndex;
    } else if (mode === 'subscription' && this._lastSubCardIndex !== null) {
      // Tab toggle back to subscription — restore the last chosen card
      target = newCards[this._lastSubCardIndex] ?? newCards[0];
    } else if (prevTitle) {
      // Tab toggle: try to keep the same size selected by matching title
      target = newCards.find(
        (c) => c.querySelector('.mp-atf__card-title')?.textContent.trim() === prevTitle
      ) ?? null;
    }

    if (target) {
      // Deselect all cards, then select the target
      this.cards.querySelectorAll('[data-card]').forEach((c) => {
        c.classList.remove('is-selected');
        const radio = c.querySelector('[data-card-radio]');
        if (radio) radio.checked = false;
      });
      target.classList.add('is-selected');
      const radio = target.querySelector('[data-card-radio]');
      if (radio) radio.checked = true;
      this._syncFromSelected();
    } else {
      // Fallback: pick first available card for the mode
      this._ensureValidSelection();
      this._syncFromSelected();
    }
  }

  /** Selects a card and deselects all others. */
  _selectCard(card) {
    if (card.dataset.cardMode !== this.mode) return;
    this.cards.querySelectorAll('[data-card]').forEach((c) => {
      const isThis = c === card && c.dataset.cardMode === this.mode;
      c.classList.toggle('is-selected', isThis);
      const radio = c.querySelector('[data-card-radio]');
      if (radio) radio.checked = c === card;
    });

    // Remember which subscription card was last chosen so the tab toggle
    // can restore it when switching back to subscription mode
    if (this.mode === 'subscription') {
      const subCards = Array.from(
        this.cards.querySelectorAll('[data-card][data-card-mode="subscription"]')
      ).filter((c) => !c.classList.contains('is-soldout'));
      const idx = subCards.indexOf(card);
      if (idx !== -1) this._lastSubCardIndex = idx;
    }

    this._syncFromSelected();
  }

  /** Syncs hidden inputs and ATC price from the currently selected card. */
  _syncFromSelected() {
    const visible = this._visibleCards();
    const card = visible.find((c) => c.classList.contains('is-selected')) || visible[0];
    if (!card) return;

    this.variantInput.value = card.dataset.variantId;
    this.planInput.value    = this.mode === 'subscription'
      ? (card.dataset.sellingPlanId || '')
      : '';

    const price = this.mode === 'subscription'
      ? Number(card.dataset.subPrice)
      : Number(card.dataset.oneTimePrice);

    if (this.atcPriceEl && !Number.isNaN(price)) {
      this.atcPriceEl.textContent = this._formatMoney(price);
    }

    // Update URL without reload
    if (this.root.dataset.url && card.dataset.variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', card.dataset.variantId);
      window.history.replaceState({}, '', url.toString());
    }
  }

  /* ── Gallery ─────────────────────────────────────────────────────────── */
  _showMedia(id) {
    this.mediaItems.forEach((m) => {
      const match = m.dataset.mediaId === id;
      m.classList.toggle('is-hidden', !match);
      m.setAttribute('aria-hidden', match ? 'false' : 'true');
    });
    this.thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.thumbId === id));
  }

  /* ── Reviews scroll ──────────────────────────────────────────────────── */
  // _scrollToReviews() {
  //   const target = document.getElementById('reviews');
  //   if (!target) return;
  //   window.scrollTo({
  //     top: target.getBoundingClientRect().top + window.pageYOffset - 100,
  //     behavior: 'smooth',
  //   });
  // }

  /* ── Utility ─────────────────────────────────────────────────────────── */
  _formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    const dollars = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
    return '$' + dollars;
  }
}

/* ── Initialise one AtfSection per section on the page ─────────────────── */
document.querySelectorAll('.mp-atf-section').forEach((el) => {
  if (!el.dataset.atfInit) {
    el.dataset.atfInit = '1';
    new AtfSection(el);
  }
});


/* =============================================================================
   2. initMobileZoom
   Full-screen zoom overlay for the mobile gallery (.mobile-gallery .glide__slide).
   Tap a slide image to open. Inside the overlay:
     – Pinch to zoom in/out (1×–5×)
     – Double-tap to zoom in to 2.5× or reset
     – Drag to pan when zoomed in
     – Swipe down (>120 px) or tap × to close
   Only active when window width < 1080 px (desktop gallery is hidden anyway).
   Scoped to .mp-atf-section; no other sections are affected.
============================================================================= */

(function initMobileZoom() {
  var MOBILE_MAX = 1080;
  var MAX_SCALE  = 5;

  /* ── Build overlay once ──────────────────────────────────────────────── */
  var overlay = document.createElement('div');
  overlay.className = 'atf-zoom-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Image zoom');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML =
    '<button class="atf-zoom-overlay__close" aria-label="Close image zoom">×</button>' +
    '<button class="atf-zoom-overlay__nav atf-zoom-overlay__nav--prev" aria-label="Previous image">' +
      '<span aria-hidden="true">‹</span></button>' +
    '<img class="atf-zoom-overlay__img" src="" alt="">' +
    '<button class="atf-zoom-overlay__nav atf-zoom-overlay__nav--next" aria-label="Next image">' +
      '<span aria-hidden="true">›</span></button>';
  document.body.appendChild(overlay);

  var lastFocused = null; // element to restore focus to when the dialog closes

  var closeBtn = overlay.querySelector('.atf-zoom-overlay__close');
  var zoomImg  = overlay.querySelector('.atf-zoom-overlay__img');
  var prevBtn  = overlay.querySelector('.atf-zoom-overlay__nav--prev');
  var nextBtn  = overlay.querySelector('.atf-zoom-overlay__nav--next');

  /* Show the arrows only when there's more than one image, and disable the one
     that would run past an end. Called on open and after each navigation. */
  function updateNav() {
    var multi = galleryImgs.length > 1;
    prevBtn.style.display = multi ? '' : 'none';
    nextBtn.style.display = multi ? '' : 'none';
    prevBtn.disabled = currentIndex <= 0;
    nextBtn.disabled = currentIndex >= galleryImgs.length - 1;
  }

  /* ── State ───────────────────────────────────────────────────────────── */
  var scale = 1, tx = 0, ty = 0;
  var lastTap = 0;
  // pinch
  var pinchInitDist = 0, pinchInitScale = 1;
  var pinchInitMidX = 0, pinchInitMidY = 0, pinchInitTx = 0, pinchInitTy = 0;
  // pan
  var panStartX = 0, panStartY = 0, panBaseTx = 0, panBaseTy = 0;
  var isPanning = false;
  // gallery navigation (swipe left/right between images)
  var galleryImgs   = [];   // [{ src, alt }]
  var currentIndex  = 0;
  var gestureAxis   = null; // 'h' | 'v' — locked on the first significant 1-finger move at scale 1
  var isNavigating  = false; // guards against overlapping slide animations

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  function clampTranslate() {
    var maxX = Math.max(0, (zoomImg.offsetWidth  * scale - window.innerWidth)  / 2);
    var maxY = Math.max(0, (zoomImg.offsetHeight * scale - window.innerHeight) / 2);
    tx = clamp(tx, -maxX, maxX);
    ty = clamp(ty, -maxY, maxY);
  }

  function applyTransform(animate) {
    zoomImg.style.transition = animate
      ? 'transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1)'
      : 'none';
    zoomImg.style.transform  = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
  }

  function resetTransform(animate) {
    scale = 1; tx = 0; ty = 0;
    applyTransform(animate);
    overlay.style.background = '';
  }

  /* Pick the highest-res src available on an <img>. */
  function bestSrc(imgEl) {
    if (imgEl.srcset) {
      var best = imgEl.srcset.split(',').reduce(function (acc, s) {
        var parts = s.trim().split(/\s+/);
        var w = parseInt(parts[1]) || 0;
        return w > acc.w ? { url: parts[0], w: w } : acc;
      }, { url: '', w: 0 });
      if (best.url) return best.url;
    }
    return imgEl.src;
  }

  /* ── Open / close ────────────────────────────────────────────────────── */
  function openOverlay(imgs, index) {
    galleryImgs  = imgs;
    currentIndex = index;
    gestureAxis  = null;
    isNavigating = false;
    lastFocused = document.activeElement;
    scale = 1; tx = 0; ty = 0;
    zoomImg.style.transform  = '';
    zoomImg.style.transition = '';
    overlay.style.background = '';
    var item = galleryImgs[currentIndex] || { src: '', alt: '' };
    zoomImg.src = item.src;
    zoomImg.alt = item.alt || '';
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    updateNav();
    // Move focus into the dialog so AT/keyboard users land on a control.
    closeBtn.focus();
  }

  /* Slide to the previous/next image. dir = +1 (next) or -1 (prev).
     Animates the current image out, swaps the src, then slides the new one in.
     Bounces back at the ends. */
  function navigate(dir) {
    // Ignore input while a slide is already animating — don't disturb it.
    if (isNavigating) return;
    var newIndex = currentIndex + dir;
    if (newIndex < 0 || newIndex >= galleryImgs.length) {
      // No image in that direction — spring back to centre.
      tx = 0; ty = 0; applyTransform(true);
      return;
    }
    isNavigating = true;
    var w = window.innerWidth;
    var item = galleryImgs[newIndex] || { src: '', alt: '' };

    // Preload the incoming image up front so it's decoded before we show it —
    // this prevents the old image from lingering during the slide-in (the
    // "overlap" glitch).
    var preload = new Image();
    var ready = false;
    preload.onload = preload.onerror = function () { ready = true; };
    preload.src = item.src;

    // 1. Slide the current image off-screen in the swipe direction.
    tx = -dir * w;
    applyTransform(true);

    setTimeout(function () {
      // 2. Jump (still showing the OLD image, but off-screen so it's invisible)
      //    to the incoming side.
      currentIndex = newIndex;
      scale = 1; ty = 0; tx = dir * w;
      applyTransform(false);
      updateNav();

      // 3. Swap the src while off-screen, then glide to centre once it's ready.
      function slideIn() {
        zoomImg.src = item.src;
        zoomImg.alt = item.alt || '';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            tx = 0;
            applyTransform(true);
            isNavigating = false;
          });
        });
      }

      if (ready) {
        slideIn();
      } else {
        // Wait for the preloaded image; cap the wait so a slow/failed load
        // never leaves the overlay stuck.
        var waited = 0;
        var poll = setInterval(function () {
          waited += 30;
          if (ready || waited >= 600) { clearInterval(poll); slideIn(); }
        }, 30);
      }
    }, 280); // match the 0.28s slide-out transition
  }

  function closeOverlay() {
    overlay.classList.remove('is-open', 'is-gesturing');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    resetTransform(false);
    // Restore focus to whatever opened the dialog.
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  /* Is this touch/click on one of the overlay's buttons (close / prev / next)?
     Those handle themselves, so the pan/pinch/swipe logic must ignore them. */
  function isControl(target) {
    return !!(target && target.closest && target.closest('.atf-zoom-overlay__close, .atf-zoom-overlay__nav'));
  }

  /* ── Touch interaction inside overlay ───────────────────────────────── */
  overlay.addEventListener('touchstart', function (e) {
    if (isControl(e.target)) return;

    if (e.touches.length === 2) {
      var t1 = e.touches[0], t2 = e.touches[1];
      pinchInitDist  = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      pinchInitScale = scale;
      pinchInitMidX  = (t1.clientX + t2.clientX) / 2;
      pinchInitMidY  = (t1.clientY + t2.clientY) / 2;
      pinchInitTx    = tx;
      pinchInitTy    = ty;
      isPanning      = false;
      e.preventDefault();
      return;
    }

    if (e.touches.length === 1) {
      var t = e.touches[0];
      var now = Date.now();

      if (now - lastTap < 300 && lastTap > 0) {
        // Double-tap: zoom in towards tap point, or reset
        lastTap = 0;
        if (scale > 1) {
          resetTransform(true);
        } else {
          scale = 2.5;
          tx = (t.clientX - window.innerWidth  / 2) * (1 - scale);
          ty = (t.clientY - window.innerHeight / 2) * (1 - scale);
          clampTranslate();
          applyTransform(true);
        }
        e.preventDefault();
        return;
      }
      lastTap = now;

      panStartX  = t.clientX;
      panStartY  = t.clientY;
      panBaseTx  = tx;
      panBaseTy  = ty;
      isPanning  = (scale > 1);
      gestureAxis = null;
      overlay.classList.add('is-gesturing');
    }
  }, { passive: false });

  overlay.addEventListener('touchmove', function (e) {
    if (isControl(e.target)) return;

    if (e.touches.length === 2) {
      var t1 = e.touches[0], t2 = e.touches[1];
      var dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      var midX = (t1.clientX + t2.clientX) / 2;
      var midY = (t1.clientY + t2.clientY) / 2;

      scale = clamp(pinchInitScale * (dist / pinchInitDist), 1, MAX_SCALE);
      tx    = pinchInitTx + (midX - pinchInitMidX);
      ty    = pinchInitTy + (midY - pinchInitMidY);
      clampTranslate();
      applyTransform(false);
      e.preventDefault();
      return;
    }

    if (e.touches.length === 1) {
      if (isNavigating) { e.preventDefault(); return; }
      var t = e.touches[0];
      var dx = t.clientX - panStartX;
      var dy = t.clientY - panStartY;

      if (isPanning) {
        // Zoomed in: pan around the image.
        tx = panBaseTx + dx;
        ty = panBaseTy + dy;
        clampTranslate();
        applyTransform(false);
        e.preventDefault();
        return;
      }

      // At scale 1: lock the gesture to an axis on the first real movement so a
      // horizontal drag swipes between images and a downward drag closes.
      if (!gestureAxis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        gestureAxis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
      }

      if (gestureAxis === 'h' && galleryImgs.length > 1) {
        // Horizontal swipe: drag the current image with the finger.
        tx = dx; ty = 0;
        zoomImg.style.transition = 'none';
        zoomImg.style.transform  = 'translate(' + tx + 'px,0) scale(1)';
        e.preventDefault();
      } else if (gestureAxis === 'v' && dy > 0) {
        // Swipe-down-to-close gesture.
        ty = panBaseTy + dy;
        var progress = Math.min(dy / 200, 1);
        overlay.style.background = 'rgba(0,0,0,' + (0.92 * (1 - progress * 0.75)) + ')';
        zoomImg.style.transition = 'none';
        zoomImg.style.transform  = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
        e.preventDefault();
      }
    }
  }, { passive: false });

  overlay.addEventListener('touchend', function (e) {
    if (isControl(e.target)) return;
    overlay.classList.remove('is-gesturing');
    isPanning = false;

    // Horizontal swipe: move to the previous/next image if dragged far enough,
    // otherwise spring back to the current one.
    if (e.touches.length === 0 && scale <= 1 && gestureAxis === 'h' && galleryImgs.length > 1) {
      gestureAxis = null;
      var threshold = Math.min(80, window.innerWidth * 0.2);
      if (tx <= -threshold) { navigate(1); return; }   // swipe left → next
      if (tx >=  threshold) { navigate(-1); return; }   // swipe right → prev
      tx = 0; ty = 0; applyTransform(true);             // not far enough → snap back
      return;
    }
    gestureAxis = null;

    // Swipe-down threshold
    if (e.touches.length === 0 && scale <= 1 && ty > 120) {
      closeOverlay();
      return;
    }

    // Pinch collapsed to < 1
    if (scale < 1) {
      resetTransform(true);
      return;
    }

    overlay.style.background = '';
    clampTranslate();
    applyTransform(true);
  }, { passive: true });

  // Tap the dark backdrop → close. Tapping the image itself must NOT close it.
  // The image has pointer-events:none, so its taps also land on the overlay —
  // so hit-test the image's box and only close when the tap is outside it.
  overlay.addEventListener('click', function (e) {
    if (e.target !== overlay) return;
    var r = zoomImg.getBoundingClientRect();
    var onImage = e.clientX >= r.left && e.clientX <= r.right &&
                  e.clientY >= r.top  && e.clientY <= r.bottom;
    if (!onImage) closeOverlay();
  });
  closeBtn.addEventListener('click', closeOverlay);

  // Prev / next arrow buttons. When zoomed in, reset first so navigation reads
  // the un-zoomed image cleanly.
  prevBtn.addEventListener('click', function () {
    if (scale > 1) resetTransform(false);
    navigate(-1);
  });
  nextBtn.addEventListener('click', function () {
    if (scale > 1) resetTransform(false);
    navigate(1);
  });

  document.addEventListener('keydown', function (e) {
    if (!overlay.classList.contains('is-open')) return;
    if (e.key === 'Escape') { closeOverlay(); return; }
    // Arrow keys page through the gallery (only meaningful when not zoomed).
    if (scale <= 1 && galleryImgs.length > 1) {
      if (e.key === 'ArrowRight') { e.preventDefault(); navigate(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(-1); }
    }
  });

  /* ── Bind tap-to-open on mobile gallery slides ───────────────────────── */
  function bindSection(section) {
    var gallery = section.querySelector('.mobile-gallery');
    if (!gallery) return;

    gallery.addEventListener('click', function (e) {
      if (window.innerWidth >= MOBILE_MAX) return;
      var imgEl = e.target.closest('.glide__slide img');
      if (!imgEl) return;

      // Collect every (non-cloned) gallery image so the overlay can swipe
      // between them, then open at the one that was tapped.
      var imgEls = Array.from(
        gallery.querySelectorAll('.glide__slide:not(.glide__slide--clone) img')
      );
      var imgs = imgEls.map(function (el) {
        return { src: bestSrc(el), alt: el.alt };
      });
      var tappedSrc = bestSrc(imgEl);
      var index = imgs.findIndex(function (i) { return i.src === tappedSrc; });
      openOverlay(imgs, index < 0 ? 0 : index);
    });
  }

  function setup() {
    document.querySelectorAll('.mp-atf-section').forEach(bindSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
}());


/* =============================================================================
   3. initDesktopLightboxNav
   Adds previous/next navigation to the desktop image lightbox (#lightbox-modal
   in theme.liquid). The lightbox is opened by product-min.js when a gallery
   image is clicked, but it only ever shows that one image (with double-click
   zoom + touch pan). This layers on top of it — WITHOUT touching product-min.js
   — so users can move between images just like the mobile overlay:
     – ‹ / › arrow buttons
     – ArrowLeft / ArrowRight keys
     – click-drag (mouse) or swipe (touch) left/right, when not zoomed in
   It reads/writes only the shared #lightbox-img element and rebuilds the image
   list each time the lightbox opens, so it stays in sync with variant changes.
============================================================================= */

(function initDesktopLightboxNav() {
  var modal = document.getElementById('lightbox-modal');
  var img   = document.getElementById('lightbox-img');
  if (!modal || !img) return;

  var imgs  = [];  // ordered gallery <img> elements currently backing the lightbox
  var index = 0;

  /* Collect the gallery images that feed the lightbox, in document order.
     product-min.js binds the lightbox to `.zoom-container img`; scope to the
     first media-gallery so a duplicated (variant) gallery doesn't double them. */
  function collectImages() {
    var scope = document.querySelector('media-gallery') || document;
    var els = Array.from(scope.querySelectorAll('.zoom-container img'));
    if (!els.length) els = Array.from(document.querySelectorAll('.zoom-container img'));
    // De-dupe by source in case a duplicate (variant) gallery is present.
    var seen = {};
    return els.filter(function (el) {
      var key = srcOf(el);
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  // Match product-min.js, which opens the lightbox with `img.src = el.src` — the
  // resolved fallback URL. Use the same property so responsive srcset images
  // still match (currentSrc could resolve to a different resolution).
  function srcOf(el) { return el.src || el.getAttribute('src') || ''; }

  /* Current zoom scale, parsed from the transform product-min.js sets on the img. */
  function currentScale() {
    var t = img.style.transform || '';
    var m = t.match(/scale\(([-0-9.]+)\)/);
    return m ? parseFloat(m[1]) : 1;
  }

  function show(i) {
    if (i < 0 || i >= imgs.length) return;
    index = i;
    var el = imgs[i];
    img.src = srcOf(el);
    img.alt = el.alt || '';
    img.style.transform = 'scale(1)'; // reset any zoom/pan from the previous image
    img.style.transition = 'none';
  }

  function go(dir) {
    if (imgs.length < 2) return;
    var next = index + dir;
    if (next < 0) next = imgs.length - 1;          // wrap around
    if (next >= imgs.length) next = 0;
    show(next);
  }

  /* ── Arrow buttons (injected once) ───────────────────────────────────────── */
  function makeArrow(dir, label) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lightbox-nav lightbox-nav--' + (dir < 0 ? 'prev' : 'next');
    btn.setAttribute('aria-label', label);
    btn.innerHTML = '<span aria-hidden="true">' + (dir < 0 ? '‹' : '›') + '</span>';
    btn.addEventListener('click', function (e) {
      e.stopPropagation();   // don't let it bubble to the backdrop-close handler
      go(dir);
    });
    return btn;
  }
  var prevBtn = makeArrow(-1, 'Previous image');
  var nextBtn = makeArrow(1, 'Next image');
  var content = modal.querySelector('.lightbox-content') || modal;
  content.appendChild(prevBtn);
  content.appendChild(nextBtn);

  /* ── Sync index whenever the lightbox opens ──────────────────────────────── */
  function refreshOnOpen() {
    imgs = collectImages();
    var openSrc = srcOf(img);
    var found = imgs.findIndex(function (el) { return srcOf(el) === openSrc; });
    index = found === -1 ? 0 : found;
    var multiple = imgs.length > 1;
    prevBtn.hidden = !multiple;
    nextBtn.hidden = !multiple;
  }

  // product-min.js opens the lightbox by adding the `active` class — watch for it.
  new MutationObserver(function () {
    if (modal.classList.contains('active')) refreshOnOpen();
  }).observe(modal, { attributes: true, attributeFilter: ['class'] });

  /* ── Keyboard navigation ─────────────────────────────────────────────────── */
  document.addEventListener('keydown', function (e) {
    if (!modal.classList.contains('active')) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
  });

  /* ── Click-drag (mouse) / swipe (touch) navigation, when not zoomed ───────── */
  var startX = 0, startY = 0, dragging = false, axis = null, pointerId = null;

  img.addEventListener('pointerdown', function (e) {
    if (currentScale() > 1.01) return;      // zoomed in → leave panning to product-min.js
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragging = true; axis = null;
    startX = e.clientX; startY = e.clientY; pointerId = e.pointerId;
  });

  img.addEventListener('pointermove', function (e) {
    if (!dragging || e.pointerId !== pointerId) return;
    var dx = e.clientX - startX;
    var dy = e.clientY - startY;

    if (!axis && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
      if (axis === 'h') { try { img.setPointerCapture(pointerId); } catch (err) {} }
    }
    if (axis === 'h') {
      img.style.transition = 'none';
      img.style.transform  = 'scale(1) translate(' + dx + 'px, 0)';
      e.preventDefault();
    }
  });

  function endDrag(e) {
    if (!dragging || (pointerId !== null && e.pointerId !== pointerId)) return;
    dragging = false;
    try { img.releasePointerCapture(pointerId); } catch (err) {}
    var wasHorizontal = axis === 'h';
    var dx = e.clientX - startX;
    pointerId = null; axis = null;
    if (!wasHorizontal) return;

    var threshold = Math.min(90, window.innerWidth * 0.12);
    if (dx <= -threshold) { go(1); }        // drag left → next
    else if (dx >= threshold) { go(-1); }   // drag right → previous
    else {
      // Not far enough — settle the image back to centre.
      img.style.transition = 'transform 0.2s ease';
      img.style.transform  = 'scale(1)';
    }
  }

  img.addEventListener('pointerup', endDrag);
  img.addEventListener('pointercancel', endDrag);
}());


/* =============================================================================
   4. initDropdowns
   Simple accordion open/close for the product detail panels.
============================================================================= */

function initDropdowns() {
  document.querySelectorAll('.mp-atf-section .product-dropdown .title-wpr').forEach((btn) => {
    btn.addEventListener('click', () => {
      const panel  = document.getElementById(btn.getAttribute('aria-controls'));
      const isOpen = btn.getAttribute('aria-expanded') === 'true';

      btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      btn.querySelector('.icon-wpr').classList.toggle('rotate', !isOpen);
      panel.hidden = isOpen;
      panel.classList.toggle('show', !isOpen);
    });
  });
}

initDropdowns();


/* =============================================================================
   5. initStickyCTA
   Sticky bottom bar: shows when the product form leaves the viewport, mirrors
   variant selection from the main form, and submits its own ATC request.
============================================================================= */

(function initStickyCTA() {
  'use strict';

  document.querySelectorAll('.mp-atf-section').forEach(function (sectionEl) {
    const sectionId  = sectionEl.dataset.section;
    const stickyCTA  = document.querySelector('#StickyCTA-' + sectionId);
    if (!stickyCTA) return;

    const productForm  = sectionEl.querySelector('.mp-atf__product-form');
    const cardsEl      = sectionEl.querySelector('[data-cards]');
    const variantInput = sectionEl.querySelector('[data-variant-input]');
    const planInput    = sectionEl.querySelector('[data-selling-plan-input]');

    const selectBtn   = stickyCTA.querySelector('[data-sticky-select-btn]');
    const selectLabel = stickyCTA.querySelector('[data-sticky-select-label]');
    const dropdown    = stickyCTA.querySelector('[data-sticky-dropdown]');
    const atcBtn      = stickyCTA.querySelector('[data-sticky-atc]');

    if (!productForm || !cardsEl || !selectBtn || !dropdown || !atcBtn) return;

    /* ── Visibility observer ────────────────────────────────────────────── */
    var formObserver = new IntersectionObserver(function (entries) {
      // Show the sticky bar once the main form scrolls out of view.
      var show = !entries[0].isIntersecting;
      stickyCTA.classList.toggle('is-visible', show);
      stickyCTA.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) {
        stickyCTA.removeAttribute('inert');
      } else {
        // While hidden the bar is only translated off-screen, so without `inert`
        // its buttons would still be tabbable. Close any open menu and remove it
        // from the tab order + accessibility tree.
        closeDropdown(false);
        stickyCTA.setAttribute('inert', '');
      }
    }, { threshold: 0.1 });

    formObserver.observe(productForm);

    /* ── Helpers ─────────────────────────────────────────────────────────── */
    function formatMoney(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
      var dollars = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
      return '$' + dollars;
    }

    function getCardPrices(card) {
      var mode    = cardsEl.dataset.purchaseMode || 'subscription';
      var compare = mode === 'subscription' ? Number(card.dataset.subCompare)   : Number(card.dataset.oneTimeCompare);
      var current = mode === 'subscription' ? Number(card.dataset.subPrice)     : Number(card.dataset.oneTimePrice);
      return {
        compare: compare > 0 ? formatMoney(compare) : '',
        current: formatMoney(current),
      };
    }

    function updateSelectLabel(title, compare, current) {
      selectLabel.innerHTML =
        '<span class="title">' + title + '<span class="mp-atf-sr-only">. </span></span>' +
        (compare ? '<span class="compare"><span class="mp-atf-sr-only">Regular price </span>' + compare + '<span class="mp-atf-sr-only">. </span></span> ' : '') +
        '<span class="current"><span class="mp-atf-sr-only">Price </span>' + current + '</span>';
    }

    function syncLabelFromSelected() {
      var mode = cardsEl.dataset.purchaseMode || 'subscription';
      var sel  = Array.from(cardsEl.querySelectorAll('[data-card].is-selected'))
                      .find(function (c) { return c.dataset.cardMode === mode; });
      if (!sel) return;
      var titleEl = sel.querySelector('.mp-atf__card-title');
      var prices  = getCardPrices(sel);
      updateSelectLabel(
        titleEl ? titleEl.textContent.trim() : '',
        prices.compare,
        prices.current
      );
    }

    /* ── Build dropdown options ──────────────────────────────────────────── */
    function buildOptions() {
      dropdown.innerHTML = '';

      var allCards    = Array.from(cardsEl.querySelectorAll('[data-card]'))
                             .filter(function (c) { return !c.classList.contains('is-soldout'); });
      var currentMode = cardsEl.dataset.purchaseMode || 'subscription';

      var groups = [
        { mode: 'subscription', label: 'Subscribe & Save' },
        { mode: 'one_time',     label: 'One Time Purchase' },
      ];

      groups.forEach(function (group) {
        var groupCards = allCards.filter(function (c) { return c.dataset.cardMode === group.mode; });
        if (!groupCards.length) return;

        // Hidden group header (kept for semantic structure / future use)
        var header = document.createElement('li');
        header.className = 'mp-sticky-cta__option-group-header';
        header.setAttribute('role', 'presentation');
        header.textContent = group.label;
        dropdown.appendChild(header);

        groupCards.forEach(function (card) {
          var titleEl    = card.querySelector('.mp-atf__card-title');
          var saveEl     = card.querySelector('.mp-atf__save-tag');
          var compareRaw = group.mode === 'subscription' ? Number(card.dataset.subCompare)   : Number(card.dataset.oneTimeCompare);
          var currentRaw = group.mode === 'subscription' ? Number(card.dataset.subPrice)     : Number(card.dataset.oneTimePrice);
          var compare    = compareRaw > 0 ? formatMoney(compareRaw) : '';
          var current    = formatMoney(currentRaw);
          var title      = titleEl ? titleEl.textContent.trim() : '';
          var saving     = saveEl  ? saveEl.textContent.trim()  : '';
          var isSelected = card.classList.contains('is-selected') && group.mode === currentMode;

          var li = document.createElement('li');
          li.className = 'mp-sticky-cta__option';
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          li.tabIndex = -1; // roving tabindex; the active option is set to 0 on open
          li.dataset.variantId = card.dataset.variantId || '';
          li.dataset.cardMode  = group.mode;

          // Deterministic accessible name so every AT reads the full option clearly.
          var ariaParts = [title, current];
          if (compare) ariaParts.push('regular price ' + compare);
          if (saving)  ariaParts.push(saving);
          li.setAttribute('aria-label', ariaParts.join(', '));

          li.innerHTML =
            '<div class="mp-sticky-cta__option-label">' +
              '<span class="mp-sticky-cta__option-title">' + title + '</span>' +
            '</div>' +
            '<div class="mp-sticky-cta__option-offer">' +
              '<div class="mp-sticky-cta__option-price">' +
                (compare ? '<span class="mp-sticky-cta__option-compare"><span class="mp-atf-sr-only">Regular price </span>' + compare + '</span>' : '') +
                '<span class="mp-sticky-cta__option-current"><span class="mp-atf-sr-only">Price </span>' + current + '</span>' +
              '</div>' +
              (saving ? '<span style="display:flex;justify-content:flex-end"><span class="mp-sticky-cta__option-save">' + saving + '</span></span>' : '') +
            '</div>';

          li.addEventListener('click', function () { selectOption(li, card, group.mode); });
          dropdown.appendChild(li);
        });
      });
    }

    function selectOption(li, card, mode) {
      // Switch mode on main section if needed
      var currentMode = cardsEl.dataset.purchaseMode || 'subscription';
      if (mode && mode !== currentMode) {
        var toggleBtn = sectionEl.querySelector('[data-purchase-toggle] [data-purchase-type="' + mode + '"]');
        if (toggleBtn) toggleBtn.click();
      }

      // Select the card in the main form
      card.click();

      dropdown.querySelectorAll('[role="option"]')
        .forEach(function (o) { o.setAttribute('aria-selected', 'false'); });
      li.setAttribute('aria-selected', 'true');

      var titleEl    = card.querySelector('.mp-atf__card-title');
      var compareRaw = mode === 'subscription' ? Number(card.dataset.subCompare)   : Number(card.dataset.oneTimeCompare);
      var currentRaw = mode === 'subscription' ? Number(card.dataset.subPrice)     : Number(card.dataset.oneTimePrice);

      updateSelectLabel(
        titleEl ? titleEl.textContent.trim() : '',
        compareRaw > 0 ? formatMoney(compareRaw) : '',
        formatMoney(currentRaw)
      );

      closeDropdown(true);
    }

    /* ── Dropdown open / close + keyboard (listbox pattern) ──────────────── */
    function optionEls() {
      return Array.from(dropdown.querySelectorAll('[role="option"]'));
    }

    function focusOption(idx) {
      var opts = optionEls();
      if (!opts.length) return;
      idx = Math.max(0, Math.min(idx, opts.length - 1));
      opts.forEach(function (o, i) { o.tabIndex = i === idx ? 0 : -1; });
      opts[idx].focus();
    }

    function openDropdown() {
      buildOptions();
      dropdown.hidden = false;
      selectBtn.setAttribute('aria-expanded', 'true');
      var opts = optionEls();
      var selected = opts.findIndex(function (o) { return o.getAttribute('aria-selected') === 'true'; });
      focusOption(selected === -1 ? 0 : selected);
    }

    function closeDropdown(returnFocus) {
      var wasOpen = !dropdown.hidden;
      dropdown.hidden = true;
      selectBtn.setAttribute('aria-expanded', 'false');
      if (returnFocus && wasOpen) selectBtn.focus();
    }

    selectBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      selectBtn.getAttribute('aria-expanded') === 'true' ? closeDropdown(false) : openDropdown();
    });

    // Open the menu with the keyboard from the trigger.
    selectBtn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (selectBtn.getAttribute('aria-expanded') !== 'true') openDropdown();
      }
    });

    // Roving-focus navigation inside the listbox.
    dropdown.addEventListener('keydown', function (e) {
      var opts = optionEls();
      if (!opts.length) return;
      var current = opts.indexOf(document.activeElement);
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); focusOption(current + 1); break;
        case 'ArrowUp':   e.preventDefault(); focusOption(current - 1); break;
        case 'Home':      e.preventDefault(); focusOption(0); break;
        case 'End':       e.preventDefault(); focusOption(opts.length - 1); break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (current !== -1) opts[current].click();
          break;
        case 'Escape':
          e.preventDefault();
          closeDropdown(true);
          break;
        case 'Tab':
          // Close and return focus to the trigger so the browser's default Tab
          // continues from there (avoids focus being stranded in a hidden list).
          closeDropdown(false);
          selectBtn.focus();
          break;
      }
    });

    document.addEventListener('click',  function (e) { if (!stickyCTA.contains(e.target)) closeDropdown(false); });
    document.addEventListener('scroll', function ()  { closeDropdown(false); });

    /* ── Keep label in sync when main cards change ───────────────────────── */
    var moObserver = new MutationObserver(function () {
      if (dropdown.hidden) syncLabelFromSelected();
    });
    moObserver.observe(cardsEl, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-purchase-mode'],
    });

    /* ── Sticky ATC submit ───────────────────────────────────────────────── */
    atcBtn.addEventListener('click', async function () {
      var varId  = variantInput?.value;
      var planId = planInput?.value;
      if (!varId) return;

      atcBtn.disabled = true;
      var main = { id: parseInt(varId, 10), quantity: 1 };
      if (planId) main.selling_plan = parseInt(planId, 10);

      try {
        var items = (window.GiftBundle && window.GiftBundle.augment)
          ? await window.GiftBundle.augment([main])
          : [main];

        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ items: items }),
        });

        // Retry with just the product if the bundled gift made the atomic add fail.
        if (!res.ok && items.length > 1) {
          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({ items: [main] }),
          });
        }

        if (res.ok) {
          window.location.href = '/cart';
        } else {
          var data = await res.json();
          alert(data.description || 'Could not add to cart.');
        }
      } catch (err) {
        console.error('Sticky ATC error:', err);
      } finally {
        atcBtn.disabled = false;
      }
    });

    // Initial label sync
    syncLabelFromSelected();
  });
})();